import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { readSession } from '../_helpers/session.js';
import { analyticsEnabled, identifyUser, loadAnalytics, resetAnalytics } from '../_helpers/analytics.js';
import config from '../config.js';

/**
 * Keeps PostHog (ENG-857, `_helpers/analytics.js`) in step with the session.
 * Like LocalePreferenceSync it sits inside the Router and re-checks the
 * session on every route change, since every sign-in and sign-out ends in one:
 * a new session is identified from `GET /me`, and a session that has gone
 * (signed out, or expired and sent to /login) resets.
 *
 * Renders nothing. Without a PostHog key it does nothing at all, and it never
 * starts on the OBS overlay (`/obs/...`).
 */
export function AnalyticsSync() {
  const { pathname } = useLocation();
  const fetchWrapperRef = useRef(useFetchWrapper());
  // The JWT identified last, so each session is identified once per page load.
  const identifiedRef = useRef(null);

  useEffect(() => {
    if (!analyticsEnabled() || pathname.startsWith('/obs/')) return undefined;
    loadAnalytics();

    let jwt = null;
    try {
      jwt = readSession() ? sessionStorage.getItem('jwt') : null;
    } catch {
      jwt = null;
    }

    if (!jwt) {
      identifiedRef.current = null;
      resetAnalytics();
      return undefined;
    }
    if (identifiedRef.current === jwt) return undefined;
    identifiedRef.current = jwt;

    // Not cancelled by a later route change: the answer still holds while the
    // same session does, and only a session that has gone since drops it.
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me`)
      .then((me) => {
        if (identifiedRef.current === jwt) identifyUser(me);
      })
      .catch(() => {
        // Not identified this time; the next route change asks again.
        if (identifiedRef.current === jwt) identifiedRef.current = null;
      });
    return undefined;
  }, [pathname]);

  return null;
}

export default AnalyticsSync;
